import TransactionConstants from '@common/constants/TransactionConstants';
import { centsToDollars } from '@common/paymentsUtils/currencyUtils';
import type { ITransactionModel, MoveInCostsInterface } from '@rental-manager/models/Types';
import type { MoveInCostsSettingsInterface } from '@rental-manager/pages/Dashboard/types';
import { formatDate } from '@common/paymentsUtils/dateUtils';

export const findMoveInCostsPaymentTransactions = (
  transactions: Array<ITransactionModel>,
): ITransactionModel | undefined => {
  return transactions.find(
    (transaction) => transaction.paymentCategory === TransactionConstants.CATEGORY.MOVE_IN_COSTS,
  );
};

export const isHighRiskOTP = (moveInCostsSettings: MoveInCostsSettingsInterface): boolean => {
  return (
    moveInCostsSettings.securityDepositSettings?.securityDepositPaymentAmount > 0 ||
    moveInCostsSettings.proratedRentSettings?.proratedRentPaymentAmount > 0
  );
};

export type DetailRow = string | { label: string; value: string };

export const getMoveInCost = (moveInCostsSettings: MoveInCostsSettingsInterface): Array<DetailRow> => {
  const { securityDepositSettings, proratedRentSettings, otherSettings, dueDate } = moveInCostsSettings;

  const { securityDepositPaymentAmount } = securityDepositSettings;
  const { proratedRentPaymentAmount } = proratedRentSettings;
  let otherTotalPaymentAmount = 0;
  const otherSettingSummaryDisplay: Array<DetailRow> = [];

  otherSettings.forEach((setting) => {
    otherSettingSummaryDisplay.push({ label: setting.otherFeeType, value: centsToDollars(setting.otherPaymentAmount) });
    otherTotalPaymentAmount += setting.otherPaymentAmount;
  });
  const totalPaymentAmount = securityDepositPaymentAmount + proratedRentPaymentAmount + otherTotalPaymentAmount;

  if (
    proratedRentSettings.proratedRentStartsOpen === true ||
    securityDepositSettings.securityDepositStartsOpen === true ||
    otherSettings.length !== 0
  ) {
    return [
      { label: 'Total', value: centsToDollars(totalPaymentAmount) },
      { label: 'Security deposit', value: centsToDollars(securityDepositPaymentAmount) },
      { label: 'Prorated rent', value: centsToDollars(proratedRentPaymentAmount) },
      ...otherSettingSummaryDisplay,
      { label: 'Due date', value: formatDate(dueDate, 'MMMM d, yyyy') },
    ];
  }
  return ['No Move-in costs collected'];
};

export const generateMoveInCostPayload = (
  moveInCostsSettings: MoveInCostsSettingsInterface,
): Array<MoveInCostsInterface> => {
  const { securityDepositSettings, proratedRentSettings, otherSettings } = moveInCostsSettings;
  const { proratedRentNotes, proratedRentPaymentAmount } = proratedRentSettings;
  const { securityDepositNotes, securityDepositPaymentAmount } = securityDepositSettings;
  const moveInCosts: Array<MoveInCostsInterface> = [];

  if (securityDepositSettings.securityDepositStartsOpen === true) {
    moveInCosts.push({
      type: 'deposit',
      amount: securityDepositPaymentAmount,
      notes: securityDepositNotes,
    });
  }

  if (proratedRentSettings.proratedRentStartsOpen === true) {
    moveInCosts.push({
      type: 'prorated-rent',
      amount: proratedRentPaymentAmount,
      notes: proratedRentNotes,
    });
  }

  otherSettings.forEach((settings) => {
    moveInCosts.push({
      type: 'other',
      subType: settings.otherFeeType,
      amount: settings.otherPaymentAmount,
      notes: settings.otherNotes,
    });
  });
  return moveInCosts;
};

export const findMoveInCostsTransactionByRentTermsKey = (
  transactions: Array<Record<string, any>>,
  rentTermsKey: string,
): Record<string, any> | undefined => {
  return transactions
    .filter((transaction) => transaction.paymentCategory === TransactionConstants.CATEGORY.MOVE_IN_COSTS)
    .filter((transaction) => transaction.rentTermsKey === rentTermsKey)
    .pop();
};

export const getMoveInCostsDetails = (allTransactions, rentTermsKey): Record<string, any> => {
  const { upcomingTransactions, completedTransactions, pastDueTransactions } = allTransactions;
  const moveInCostsInfo =
    findMoveInCostsTransactionByRentTermsKey(upcomingTransactions, rentTermsKey) ||
    findMoveInCostsTransactionByRentTermsKey(completedTransactions, rentTermsKey) ||
    findMoveInCostsTransactionByRentTermsKey(pastDueTransactions, rentTermsKey) ||
    {};
  return moveInCostsInfo;
};
